import { useCallback, useState } from 'react'
import { ReasoningBlock } from '@/components'
import { useSSE } from '../../contexts/SSEContext'
import { SSE_EVENTS } from '../../contexts/SSEEvents'
import CodeStreamSnippet from './CodeStreamSnippet'
import { appendCodeTail } from './codeStreamTail'
import type { CodeGenPhase } from './codeGenerationPhase'

/**
 * Everything that streams under a running "Generating Trading Code" card: the
 * model's reasoning summary while it thinks, then the code as it is written.
 *
 * One SSE subscription for the row, keyed on the tool message id. The reasoning
 * is a snapshot (each frame carries the whole summary so far, so state is a
 * replace); the code is a tail of the last lines. The same ReasoningBlock the
 * assistant's messages use shows the thinking: open while the model thinks,
 * collapsed to "Thought for Ns" the moment the first code line lands. It renders
 * nothing when the provider sends no summary, which is common.
 *
 * Purely visual. Nothing is stored anywhere: the card mounts this only while it
 * is running, so done, failure and Stop unmount it and the state goes with it.
 * After a reload mid-generation it catches up on the next frame.
 *
 * Needs SSEProvider — useSSE throws without one — which the chat always has and
 * the card's own non-live tests never reach.
 */
interface CodeGenerationLiveProps {
  /** The tool row's chat message id: the `msgId` the server stamps on each frame. */
  msgId: string
  /** Called from the SSE handler (an event handler, not an effect) when a frame implies a phase. */
  onPhase?: (phase: CodeGenPhase) => void
}

interface LiveFrame {
  event?: string
  msgId?: number | string
  content?: string
  accumulated?: string
}

export default function CodeGenerationLive({ msgId, onPhase }: CodeGenerationLiveProps) {
  const [reasoning, setReasoning] = useState('')
  const [lines, setLines] = useState<string[]>([])

  // Both stable, so useSSE subscribes exactly once for this row.
  const filter = useCallback(
    (m: LiveFrame) =>
      (m.event === SSE_EVENTS.BOT_MESSAGE_CODE_REASONING ||
        m.event === SSE_EVENTS.BOT_MESSAGE_CODE_CHUNK) &&
      String(m.msgId) === msgId,
    [msgId],
  )
  const onFrame = useCallback(
    (m: LiveFrame) => {
      if (m.event === SSE_EVENTS.BOT_MESSAGE_CODE_REASONING) {
        const { accumulated } = m
        if (typeof accumulated !== 'string') return
        setReasoning(accumulated)
        onPhase?.('thinking')
        return
      }
      const { content } = m
      if (typeof content !== 'string') return
      setLines((prev) => appendCodeTail(prev, content))
      onPhase?.('writing')
    },
    [onPhase],
  )
  useSSE(filter, onFrame)

  return (
    <>
      <ReasoningBlock
        className="pl-5 pt-1.5"
        reasoning={reasoning}
        streaming
        hasContent={lines.length > 0}
      />
      <CodeStreamSnippet lines={lines} />
    </>
  )
}
